import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, Crown, Loader2, ArrowRight, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * PaywallPrompt - shown after the free Job Match analysis.
 * Only renders when the user is on the Free plan.
 *
 * Props:
 *  - matchScore?: number 0-100 (optional, shown in the header)
 *  - onActivated?: () => void  (called after a successful activation)
 */
export default function PaywallPrompt({ matchScore, onActivated }) {
  const [planId, setPlanId] = useState(null);
  const [passPlan, setPassPlan] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, p] = await Promise.all([
          api.get("/billing/status"),
          api.get("/billing/plans"),
        ]);
        setPlanId(s.data.subscription.plan_id);
        setPassPlan(p.data.plans.find((x) => x.id === "interview_ready_pass"));
      } catch {
        setPlanId("free");
      }
    })();
  }, []);

  // Only show on Free plan
  if (planId !== "free") return null;

  const doCheckout = async (id) => {
    setBusy(id);
    setMsg("");
    setErr("");
    try {
      const { data } = await api.post(`/billing/checkout/${id}`, {});
      if (data.kind === "beta_activated") {
        setMsg("Unlocked. Enjoy your Interview Ready Pass.");
        onActivated && onActivated();
      } else {
        setMsg(data.message || "Request received.");
      }
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(null);
    }
  };

  const rounded = typeof matchScore === "number" ? Math.round(matchScore) : null;
  // Score-aware status line — uses the app's existing 80/55 bands (Dashboard
  // scoreTone / history chips). Framed around Job Match, never readiness.
  const statusLine =
    rounded === null
      ? "Your interview preparation starts here."
      : rounded >= 80
        ? "A strong match for this role."
        : rounded >= 55
          ? "A solid foundation — now close the gaps."
          : "Not there yet — this is where preparation pays off.";
  const headlineLine =
    rounded !== null && rounded < 55
      ? "Turn this score around with focused preparation."
      : "Turn this estimate into a real advantage.";

  return (
    <div
      data-testid="paywall-prompt"
      className="relative mt-6 p-6 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/[0.08] via-zinc-900/50 to-zinc-950/70 shadow-[0_0_40px_-15px_rgba(245,158,11,0.5)] overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col md:flex-row md:items-center gap-6">
        {rounded !== null && (
          <div className="flex-shrink-0 flex items-center gap-4">
            <div className="relative h-20 w-20 rounded-2xl border border-amber-500/40 bg-zinc-950/60 flex items-center justify-center">
              <div className="text-3xl font-bold text-amber-300 tracking-tight leading-none">{rounded}</div>
            </div>
            <div className="md:hidden">
              <div className="text-[10px] uppercase tracking-widest text-amber-400">This role</div>
              <div className="text-sm font-semibold text-zinc-100">Job Match Score</div>
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-amber-400 border border-amber-500/25 bg-amber-500/[0.06] rounded-full px-2.5 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400 pulse-dot" />
            <span data-testid="paywall-status-line">{statusLine}</span>
          </div>
          <h3 className="mt-3 text-xl md:text-2xl font-bold text-zinc-50 tracking-tight leading-tight">
            {headlineLine}
          </h3>
          <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
            Unlock the Interview Ready Pass to run unlimited mock interviews, receive full coach
            feedback, and generate the printable Interview Day Brief, all tailored to this specific role.
          </p>

          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs text-zinc-300">
            {(passPlan?.features || [
              "Unlimited Mock Interviews",
              "Unlimited AI Coaching",
              "Full Interview Day Brief",
              "Company-specific preparation",
              "Follow-up questions",
              "Official Readiness Score",
            ]).map((f) => (
              <li key={f} className="flex items-start gap-2">
                <Check className="h-3 w-3 text-amber-400 mt-0.5 flex-shrink-0" strokeWidth={3} />
                {f}
              </li>
            ))}
          </ul>

          {msg && (
            <div className="mt-4 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-lg px-3 py-2">
              {msg}
            </div>
          )}
          {err && (
            <div className="mt-4 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-lg px-3 py-2">
              {err}
            </div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button
              onClick={() => doCheckout("interview_ready_pass")}
              data-testid="paywall-unlock-pass"
              disabled={busy === "interview_ready_pass"}
              className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-5 py-2.5 shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)] disabled:opacity-60"
            >
              {busy === "interview_ready_pass" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Zap className="h-3.5 w-3.5" />
              )}
              Try Interview Ready — free during beta
            </button>
            <button
              onClick={() => doCheckout("career_pro")}
              data-testid="paywall-upgrade-pro"
              disabled={busy === "career_pro"}
              className="inline-flex items-center gap-2 text-sm border border-zinc-700 hover:border-zinc-500 text-zinc-100 rounded-lg px-4 py-2.5 disabled:opacity-60"
            >
              {busy === "career_pro" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Crown className="h-3.5 w-3.5" />
              )}
              Upgrade to Career Pro
            </button>
            <Link
              to="/pricing"
              data-testid="paywall-see-plans"
              className="text-xs text-zinc-500 hover:text-zinc-200 inline-flex items-center gap-1"
            >
              Compare plans <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="mt-3 text-[10px] text-zinc-600 tracking-wide uppercase">
            Beta access. Payment gateway launching soon.
          </div>
        </div>
      </div>
    </div>
  );
}
